/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-17 14:45:24
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, ButtonGroup, FullscreenBar, Scrollable } from '@shopify/polaris';

import { withI18n } from '@shopify/react-i18n';
import { reduxForm, getFormValues, change, reset, FieldArray } from 'redux-form';
import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import Conditions from 'src/components/containers/merchandising-v2/rule/conditions.jsx';
import Strategies from 'src/components/containers/merchandising-v2/rule/strategy';
import Preview from 'src/components/containers/merchandising-v2/rule/preview';
import SaveModal from 'src/components/views/merchandising-v2/rule/save-modal/index.jsx';

import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import { routes } from 'src/routes';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { PREVIEW_PRODUCT_PER_PAGE } from 'src/constants/merchandising-v2';
import SetupStrategy from 'src/components/views/merchandising-v2/rule/strategy/setup-strategy';

import { confirmLeavePage } from 'src/helpers';
import { waitFor } from 'src/helpers/time-helper';

import {
  initialMerchandisingV2,
  buildMerchandisingV2,
  buildRequestBodyPreview,
  getRuleType,
  removeEmptyStrategy,
  isTypeAllRule,
  getStrategyField,
  initialStrategyProducts
} from 'src/helpers/merchandising-v2-helper';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as SearchSettingsActions from 'actions/search-settings-actions';
import * as MerchandisingV2Actions from 'src/stores/actions/merchandising-v2-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as variantsDisplayActions from 'src/stores/actions/variants-display-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';
import * as productPerformanceRankingAction from 'actions/product-performance-ranking-action';

import type { ComposedProps, State } from './index.type';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

class MerchandisingV2Rule extends Component<ComposedProps, State> {
  timeoutProcessingUpdateBanner = undefined;
  unsubscribeExit = undefined;

  constructor() {
    super();
    this.merchandisingRuleStrategyRef = React.createRef();
  }

  state = {
    youtubeModal: false,
    saveModal: false,
    status: 'enabled',
    isTourOpen: false,
    previewPage: 1,
    height: 700,
    previewLimit: PREVIEW_PRODUCT_PER_PAGE,
    activeStrategy: '',
    selectedIndex: -1,
    allowSave: false,
    previewQuery: '',
    strategyTab: 1
  };

  async componentDidMount() {
    const {
      ruleType,
      getVariantsDisplay,
      getProductAttribute,
      optionsList,
      getSearchSettings,
      enableAISearch,
      flagShowRanking,
      getProductPerformanceRankingSettings,
      getMerchandisingRules,
      getMerchandisingV2Rule
    } = this.props;

    // Get rule id => git detail info rule
    const { ruleId } = this.props.match.params;
    const search = _.get(this.props, 'location.search', '');
    const splitSearch = search.split('&');
    const duplicateIdString = splitSearch.find(e => e.includes('duplicateId'));

    // Call detail rule duplicate to setting
    if (!_.isEmpty(duplicateIdString)) {
      const ruleTypeSplit = duplicateIdString.split('=');
      const duplicateId = _.get(ruleTypeSplit, '[1]', 'search');
      await this.props.duplicateMerchandisingRule(duplicateId);
    }

    // Open fullscreen
    this.unsubscribeExit = enterSABFullscreen(() => this.backToManagementPage(ruleType));

    // Get variants display settings
    getVariantsDisplay();
    // Get product performance ranking settings
    if (enableAISearch && flagShowRanking) {
      getProductPerformanceRankingSettings();
    }

    // Call API get filter data to get collections
    if (_.isEmpty(optionsList)) {
      getProductAttribute();
    }

    // Call API Get search settings, support render preview with fuzzy search
    getSearchSettings();
    const dataRule = await getMerchandisingRules('', 0, 0, ruleType);
    const rules = _.get(dataRule, 'payload.data.data', []);

    if (rules.length === 0) {
      this.setState({ isTourOpen: true });
    }
    if (!!ruleId) {
      await getMerchandisingV2Rule(ruleId);
    }

    this.renderPreview();
    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    const { resetRule } = this.props;
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
    exitSABFullscreen();
    this.unsubscribeExit();
    resetRule();
  }

  //Get merchandising when ruleType updated
  async componentWillReceiveProps(nextProps) {
    const { ruleType: ruleTypeNext, getMerchandisingRules, loading, success } = nextProps;
    const { ruleType, loading: prevLoading, checkExistsUniversalRule } = this.props;

    if (ruleTypeNext !== ruleType) {
      await getMerchandisingRules('', 0, 0, ruleTypeNext);
    }

    if (prevLoading === true && loading === false && success === true) {
      await checkExistsUniversalRule(ruleTypeNext);
    }
  }

  componentWillUpdate(nextProps, nextState) {
    const { formValues, updateField } = nextProps;
    const { activeStrategy } = this.state;
    const { activeStrategy: activeStrategyNext } = nextState;
    // Remove empty strategy when close/change setting of condition
    if (activeStrategy !== activeStrategyNext) {
      const { strategies = [] } = formValues;
      const strategiesList = removeEmptyStrategy(strategies);
      updateField('strategies', strategiesList);
    }

    const { queriesType = '', ruleType = 'search', id = '' } = this.props.formValues || {};
    const { queriesType: queriesTypeNext = '', id: nextID = '' } = formValues || {};
    if (id === nextID && queriesType !== queriesTypeNext) {
      if (isTypeAllRule(queriesTypeNext)) {
        if (ruleType === 'search') {
          updateField('queries', ['']);
        } else {
          updateField('collection', '');
        }
      }
      if (ruleType === 'search') {
        const keepStrategies = getStrategyField(ruleType, queriesTypeNext);
        const { strategies = [] } = formValues;
        updateField(
          'strategies',
          strategies.filter(s => keepStrategies.includes(s.type))
        );
      }
      this.setState({ activeStrategy: '', selectedIndex: -1 });
    }
  }

  render() {
    const { activeStrategy, selectedIndex, status, saveModal, allowSave, strategyTab } = this.state;
    const {
      loading,
      appLanguage,
      formValues,
      updateField,
      strategies,
      strategyProducts,
      optionsList,
      moneyFormat,
      history,
      dirty,
      ruleType,
      merchandisingRules,
      handleSubmit,
      renderingPreview,
      renderingPreviewSuccess,
      previewProducts,
      previewTotalProduct,
      i18n,
      collectionIdUpdating,
      enabledProductPerformanceRanking,
      loadingDuplicateTerm
    } = this.props;
    const { ruleId } = this.props.match.params;

    return (
      <div className="BC-Page__MerchandisingV2-Rule">
        {/* <form onSubmit={this.handleDefaultSubmit}> */}
        <FullscreenBar onAction={() => this.backToManagementPage(ruleType)}>
          <div className="fullscreen-title">
            <div className="card-section title-bold-normal">
              {i18n.translate(
                `AdminHTML.MerchandisingV2.Rule.${ruleId ? 'pageEditTitle' : 'pageTitle'}`
              )}
            </div>
          </div>
          <div className="fullscreen-bar">
            <ButtonGroup>
              <Button
                disabled={loading || loadingDuplicateTerm}
                onClick={() => {
                  this.openSaveModal('disabled');
                }}>
                {i18n.translate('AdminHTML.Common.Action.saveDraft')}
              </Button>
              <Button
                disabled={loading || loadingDuplicateTerm}
                primary
                onClick={() => {
                  this.openSaveModal('enabled');
                }}>
                {i18n.translate('AdminHTML.Common.Action.reviewPublic')}
              </Button>
            </ButtonGroup>
          </div>
        </FullscreenBar>
        <div className="merchandising-rule-body">
          {/* Setting rule */}
          <div className="merchandising-rule-settings">
            <Scrollable shadow style={{ maxHeight: '1000px' }}>
              {/* Set rule conditions */}
              <div className="card-section">
                <div className="set-rule-strategies-title">
                  <span className="title">
                    {i18n.translate('AdminHTML.MerchandisingV2.Rule.card1_Title')}
                  </span>
                  <div className="tooltip-highlight">
                    {i18n.translate('AdminHTML.MerchandisingV2.Rule.card1_Title_If')}
                  </div>
                </div>
                <Conditions
                  loading={loading}
                  appLanguage={appLanguage}
                  updateField={updateField}
                  ruleType={ruleType}
                  ruleId={ruleId}
                  merchandisingRules={merchandisingRules}
                  handleOpenStrategyCard={this.handleOpenStrategyCard}
                  collectionIdUpdating={collectionIdUpdating}
                />
              </div>
              {/* Set merchandising strategies */}
              <div className="card-section merchandising-strategies">
                <div className="set-rule-strategies-title">
                  <span className="title">
                    {i18n.translate('AdminHTML.MerchandisingV2.Rule.card2_Title')}
                  </span>
                  <div className="tooltip-highlight">
                    {i18n.translate('AdminHTML.MerchandisingV2.Rule.card2_Title_Then')}
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <span className="title">
                    {i18n.translate('AdminHTML.MerchandisingV2.Rule.summary_Note')}
                  </span>
                </div>
                <div className="set-rule-strategies">
                  <FieldArray
                    name="strategies"
                    component={Strategies}
                    activeStrategy={activeStrategy}
                    handleOpenStrategyCard={this.handleOpenStrategyCard}
                    strategies={strategies}
                    strategyProducts={strategyProducts}
                    formValues={formValues}
                    loading={loading}
                    updateField={updateField}
                    appLanguage={appLanguage}
                    optionsList={optionsList}
                    moneyFormat={moneyFormat}
                    renderPreview={this.renderPreview}
                    ruleType={ruleType}
                    setSelected={this.setSelectedAttribute}
                    selectedIndex={selectedIndex}
                    strategyTab={strategyTab}
                    hasProductPerformanceRanking={enabledProductPerformanceRanking}
                  />
                </div>
              </div>
            </Scrollable>
          </div>
          {/* Preview */}
          <div className="merchandising-rule-preview">
            <Preview
              formValues={formValues}
              products={previewProducts}
              total={previewTotalProduct}
              loading={renderingPreview}
              success={renderingPreviewSuccess}
              setPreviewPage={this.setPreviewPage}
              previewPage={this.state.previewPage}
              previewQuery={this.state.previewQuery}
              setPreviewQuery={query => this.setState({ previewQuery: query })}
              ruleType={ruleType}
              hasProductPerformanceRanking={enabledProductPerformanceRanking}
            />
          </div>
          {/* Setup strategy */}
          <SetupStrategy
            selectedIndex={selectedIndex}
            ruleType={ruleType}
            onChangeTab={tab => this.setState({ strategyTab: tab })}
            strategies={strategies}
            strategyProducts={strategyProducts}
            strategyTab={strategyTab}
            history={history}
            activeStrategy={activeStrategy}
            merchandisingRuleStrategyRef={this.merchandisingRuleStrategyRef}
            removeEmptyStrategies={this.removeEmptyStrategies}
            handleOpenStrategyCard={this.handleOpenStrategyCard}
            setSelectedAttribute={this.setSelectedAttribute}
          />
        </div>
        {/* Save modal */}
        <SaveModal
          formValues={formValues}
          ruleType={ruleType}
          status={status}
          handleSaveRule={handleSubmit(this.handleSaveRule.bind(this))}
          handleCancel={() => {
            this.toggleModal('saveModal');
            this.setState({ status: '' });
          }}
          hasProductPerformanceRanking={enabledProductPerformanceRanking}
          active={saveModal}
        />
        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={allowSave ? false : dirty}
          className="routerLeavingGuard"
          // Navigate function
          navigate={path =>
            history.push({
              pathname: routes.merchandising.view(),
              search: `?panelID=${ruleType}`
            })
          }
          shouldBlockNavigation={allowSave ? false : dirty}
          isAvailableFullscreen={true}
        />
        {/* </form> */}
      </div>
    );
  }

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  setSelectedAttribute = selectedIndex => {
    this.setState({ selectedIndex });
  };

  // Handle open strategy card when create or edit
  handleOpenStrategyCard = (value, isRemoveEmptyStrategies = false, tabName = '') => {
    const { activeStrategy, strategyTab } = this.state;
    const tab = tabName === 'product' ? 0 : 1;

    if (isRemoveEmptyStrategies) {
      this.removeEmptyStrategies();
    }

    // re-click button => reset un-active
    if ((activeStrategy === value && tab === strategyTab) || value === '') {
      this.setState({ activeStrategy: '', selectedIndex: -1 });
    } else this.setState({ activeStrategy: value, strategyTab: tab });
  };

  removeEmptyStrategies = () => {
    const { strategies, updateField } = this.props;
    const validStrategies = strategies
      .map(strategy => {
        if (strategy.type === 'pin') {
          return strategy;
        }
        strategy.attributes = (strategy.attributes || []).filter(a => a.value);
        if (strategy.attributes.length === 0) {
          return null;
        }
        return strategy;
      })
      .filter(s => s);
    updateField('strategies', validStrategies);
  };

  // Back to management page
  backToManagementPage = type => {
    const { history, dirty, setMerchandisingTab } = this.props;
    const { allowSave } = this.state;
    setMerchandisingTab(type === 'collection' ? 1 : 0);
    history.push({
      pathname: routes.merchandising.view(),
      search: `?panelID=${type}`
    });

    if (!dirty || allowSave) {
      exitSABFullscreen();
    }
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Open save modal
  openSaveModal = status => {
    this.setState({ status, saveModal: true });
  };

  // Save rule data setting
  async handleSaveRule(data) {
    const { ruleId } = this.props.match.params;
    const { status = '' } = this.state;
    const {
      addMerchandisingRuleV2,
      updateMerchandisingV2Rule,
      resetForm,
      markDoneStep,
      enabledProductPerformanceRanking
    } = this.props;

    this.setState({ allowSave: true });
    const saveData = buildMerchandisingV2(data, status, 'save', enabledProductPerformanceRanking);
    // Check to fetch api Add or Update
    if (ruleId) {
      const result = await updateMerchandisingV2Rule(ruleId, saveData);

      if (result.type === 'UPDATE_MERCHANDISING_V2_RULE_SUCCESS') {
        this.setState({ saveModal: false });
        // Back to management page
        this.backToManagementPage(saveData.ruleType);
        resetForm();
        // await getChecklist();
      } else {
        this.setState({ allowSave: false });
      }
    } else {
      const result = await addMerchandisingRuleV2(saveData);
      if (result.type === 'CREATE_MERCHANDISING_RULE_V2_SUCCESS') {
        const newRuleId = _.get(result, 'payload.data.data.id', '');
        // Switch page
        if (newRuleId) {
          this.setState({ saveModal: false });

          this.backToManagementPage(saveData.ruleType);
          resetForm();
        } else {
          this.setState({ allowSave: false });
        }
      }
    }

    markDoneStep('V2_SEARCH2', {
      isDone: true
    });

    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  }

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };

  renderPreview = async (page = 1, forceRender = false, query) => {
    await waitFor(300);
    const {
      formValues,
      previewMerchandisingV2Rule,
      variantsDisplaySettings,
      products = [],
      previewRequestBody,
    } = this.props;
    const { previewLimit = PREVIEW_PRODUCT_PER_PAGE } = this.state;

    // Build merchandising data
    const merchandising = buildMerchandisingV2(formValues, 'active', 'preview');
    const isEnablePerformanceRankingInRule = _.get(formValues, 'isEnablePerformanceRanking', false);

    // Render preview
    const requestBody = buildRequestBodyPreview(merchandising, {
      page,
      limit: previewLimit,
      variantsDisplay: variantsDisplaySettings,
      products,
      ignore_performance_ranking: !isEnablePerformanceRankingInRule,
      query: query || this.state.previewQuery
    });

    if (previewRequestBody !== JSON.stringify({ ...requestBody, page: 0 }) || forceRender) {
      // Set preview page
      this.setState({ previewPage: page });

      // Render preview
      previewMerchandisingV2Rule(requestBody);
    }
  };

  setPreviewPage = (page, forceRender, query) => {
    if (query && query !== this.state.previewQuery) {
      this.setState({ previewQuery: query });
    }

    this.renderPreview(page, forceRender, query);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  setMerchandisingTab: value => dispatch(MerchandisingV2Actions.setMerchandisingTab(value)),
  // Update redux-form field
  updateField: (field, value) => dispatch(change(`MerchandisingV2RuleForm`, field, value)),

  // Get available collections and data
  getProductAttribute: () => dispatch(FilterDataActions.getProductAttribute(ownProps.i18n)),

  // Get search settings
  getSearchSettings: () => dispatch(SearchSettingsActions.getSearchSettings(ownProps.i18n)),

  // Get Variants display settings
  getVariantsDisplay: () => dispatch(variantsDisplayActions.getVariantsDisplay(ownProps.i18n)),

  // Add new merchandising rule v2
  addMerchandisingRuleV2: merchandising =>
    dispatch(MerchandisingV2Actions.addMerchandisingV2Rule(merchandising, ownProps.i18n)),

  duplicateMerchandisingRule: ruleId =>
    dispatch(MerchandisingV2Actions.duplicateMerchandisingV2Rule(ruleId, ownProps.i18n)),
  // Get merchandising rule
  getMerchandisingRule: ruleId =>
    dispatch(MerchandisingV2Actions.getMerchandisingV2Rule(ruleId, ownProps.i18n)),
  // Get list merchandising rule

  getMerchandisingRules: (
    q,
    skip,
    limit,
    ruleType,
    status = '',
    enabledFromDate = '',
    enabledToDate = ''
  ) =>
    dispatch(
      MerchandisingV2Actions.getMerchandisingV2Rules(
        q,
        skip,
        limit,
        ruleType,
        status,
        enabledFromDate,
        enabledToDate,
        ownProps.i18n
      )
    ),
  // Get merchandising rule
  getMerchandisingV2Rule: ruleId =>
    dispatch(MerchandisingV2Actions.getMerchandisingV2Rule(ruleId, ownProps.i18n)),

  // Update merchandising rule
  updateMerchandisingV2Rule: (ruleId, merchandising) =>
    dispatch(
      MerchandisingV2Actions.updateMerchandisingV2Rule(
        ruleId,
        merchandising,
        undefined,
        ownProps.i18n
      )
    ),
  // Render preview
  previewMerchandisingV2Rule: requestBody =>
    dispatch(MerchandisingV2Actions.previewMerchandisingV2Rule(requestBody, ownProps.i18n)),

  // Reset rule state
  resetRule: () => dispatch(MerchandisingV2Actions.resetRule()),

  // Reset form
  resetForm: () => dispatch(reset('MerchandisingV2RuleForm')),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  checkExistsUniversalRule: ruleType =>
    dispatch(MerchandisingV2Actions.checkExistsUniversalRule(ruleType, ownProps.i18n)),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n)),
  getProductPerformanceRankingSettings: () =>
    dispatch(productPerformanceRankingAction.getSettings())
});

const mapStateToProps = (state, props) => {
  // Get merchandising rule in case of editing
  const merchandising = _.get(state.merchandisingV2, 'merchandisingRule', {});
  const collectionIdUpdating = _.get(
    state.merchandisingV2,
    'merchandisingRule.collection.value',
    ''
  );
  const ruleTypeData = _.get(merchandising, 'ruleType', null);
  // Get form values
  const formValues = getFormValues('MerchandisingV2RuleForm')(state);
  const products = _.get(state.merchandisingV2, 'products', []);

  // Get variants display setting
  const variantsDisplaySettings = _.get(state.variantsDisplay, 'variantsDisplay', {});

  // Get options list
  const optionsList = _.get(state.filterData.productAttributes, 'optionsList', []);
  const moneyFormat = _.get(state.oauth.tenantApp, 'externalInfo.money_format', '');

  const duplicateRule = _.get(state.merchandisingV2, 'duplicateRule', null);
  const ruleTypeDuplicate = _.get(duplicateRule, 'ruleType', '');

  // Get rule type by detail or param (new)
  const { ruleType: ruleTypeParam } = props.match.params;
  const ruleType = getRuleType(ruleTypeData, ruleTypeDuplicate, ruleTypeParam);

  // Flag only show for some store using PPR in Merchandising before 11/4/2024
  // From now, PPR will be removed in merchandising.
  const flagShowRanking = _.get(
    state.oauth.tenantApp,
    'settings.instantSearch.settings.performance-ranking',
    false
  );
  const enabledRanking = _.get(state.productPerformanceRanking, 'settings.enabled', false);
  const enableAISearch = _.get(
    state.oauth.tenantApp,
    'settings.instantSearch.settings.AI-synonyms',
    false
  );

  return {
    formValues,
    initialValues:
      !_.isEmpty(merchandising) || !_.isEmpty(duplicateRule)
        ? initialMerchandisingV2(merchandising, duplicateRule, enabledRanking)
        : {
            ruleType,
            name: '',
            queries: [''],
            strategies: [],
            products: initialStrategyProducts(),
            isAppliedSingularAndPlurals: true,
            isEnablePerformanceRanking: enabledRanking
          },
    name: formValues && formValues.name ? formValues.name : '',
    enableAISearch,
    enabledProductPerformanceRanking: enableAISearch && enabledRanking && flagShowRanking,
    flagShowRanking,
    strategies: formValues && formValues.strategies ? formValues.strategies : [],
    strategyProducts: formValues && formValues.products ? formValues.products : {},
    loading: _.get(state.merchandisingV2, 'loading', true),
    saving: _.get(state.merchandisingV2, 'saving', false),
    saveSuccess: _.get(state.merchandisingV2, 'saveSuccess', false),
    success: _.get(state.merchandisingV2, 'success', false),
    ruleNames: _.get(state.merchandisingV2, 'ruleNames', []),
    merchandisingRules: _.get(state.merchandisingV2, 'merchandisingRules', []),
    previewRequestBody: _.get(state.merchandisingV2, 'previewRequestBody', ''),
    duplicateRule,
    ruleType,
    scheduleSyncIn: _.get(state.merchandisingV2, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.merchandisingV2, 'waitSyncAt', 0),
    previewProducts: _.get(state.merchandisingV2, 'previewProducts', []),
    previewTotalProduct: _.get(state.merchandisingV2, 'previewTotalProduct', 0),
    renderingPreviewSuccess: _.get(state.merchandisingV2, 'renderingPreviewSuccess', 0),
    renderingPreview: _.get(state.merchandisingV2, 'renderingPreview', 0),
    loadingDuplicateTerm: _.get(state.merchandisingV2, 'loadingDuplicateTerm', false),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    variantsDisplaySettings,
    optionsList,
    moneyFormat,
    products,
    collectionIdUpdating
  };
};

const MerchandisingV2RuleForm = reduxForm({
  form: 'MerchandisingV2RuleForm', // a unique identifier for this form
  enableReinitialize: true
})(MerchandisingV2Rule);

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(MerchandisingV2RuleForm)),
  'SyncMerchandise'
);
